<template>
    <div class="quantity-chart">
        <div class="qty-toolbar">
            <group-by-selector @change="currentGroupBy = $event" :value="currentGroupBy" />
        </div>
        <div class="qty-plot">
            <line-chart :labels="labels" :datasets="datasets" :minY="minY" :maxY="maxY" :stepY="stepY"
                :styles="plotStyles" ref="chart" />
        </div>
    </div>
</template>

<script>
import GroupBySelector from "@/components/statistics/GroupBySelector";
import LineChart from "@/components/common/LineChart";
import { injuryDate } from "@/utils/Injuries";

export default {
    name: "QuantityChart",
    components: { LineChart, GroupBySelector },
    props: {
        docs: {
            type: Array,
            default: null,
        },
        type: {
            type: String,
            default: 'total_injuries',
        }
    },
    data() {
        return {
            currentGroupBy: 'daily',
            datasets: [],
            labels: [],
        }
    },
    computed: {
        lineTemplate(vm = this) {
            return {
                borderColor: '#339999',
                pointBackgroundColor: '#339999',
                pointBorderColor: vm.$vuetify.theme.currentTheme.surface,
                pointRadius: 5,
                pointHoverRadius: 6,
                pointBorderWidth: 2,
                borderWidth: 2,
                tension: 0,
            }
        },
        counts(vm = this) {
            return vm.datasets[0]?.data || []
        },
        maxCount(vm = this) {
            return vm.counts.length ? Math.max(...vm.counts) : 0
        },
        minCount(vm = this) {
            return vm.counts.length ? Math.min(...vm.counts) : 0
        },
        maxY(vm = this) {
            return vm.maxCount <= 0 ? 2 : vm.maxCount + 1
        },
        minY(vm = this) {
            return Math.max(0, vm.minCount - 1)
        },
        stepY(vm = this) {
            return Math.max(1, Math.ceil((vm.maxY - vm.minY) / 5))
        },
        plotStyles() {
            return { position: 'relative', height: '100%', width: '100%' }
        },
        groupDocs(vm = this) {
            const buckets = {}

            vm.docs.forEach(doc => {
                let date = injuryDate(doc.date)
                if (!date) return
                if (vm.currentGroupBy === 'weekly') date = date.startOf('week')
                if (vm.currentGroupBy === 'monthly') date = date.startOf('month')

                const dateString = date.format('DD MMM').replace('.', '')
                if (!buckets[dateString]) buckets[dateString] = { date, dateString, items: [] }
                buckets[dateString].items.push({
                    complexity: vm.injuryComplexityKey(doc.complexity),
                    label: `${vm.injuryTypeLabel(doc.type)} · ${vm.injuryZoneLabel(doc.zone)}`,
                })
            })

            return Object.values(buckets).sort((a, b) => a.date.valueOf() - b.date.valueOf())
        },
    },
    mounted() {
        const vm = this
        vm.loadChart()
    },
    methods: {
        matchesType(item) {
            const vm = this
            return vm.type === 'total_injuries' || `injury_${item.complexity}` === vm.type
        },
        loadChart() {
            const vm = this
            const labels = []
            const data = []
            const details = []

            vm.groupDocs.forEach(bucket => {
                const items = bucket.items.filter(vm.matchesType)
                if (!items.length) return
                labels.push(bucket.dateString)
                data.push(items.length)
                details.push(items.map(item => item.label))
            });

            const gradientFill = vm.$refs.chart.$refs.canvas.getContext('2d').createLinearGradient(0, 50, 0, 250);
            gradientFill.addColorStop(0, "rgba(51, 153, 153, 0.35)");
            gradientFill.addColorStop(1, "rgba(51, 153, 153, 0.02)");

            vm.labels = labels
            vm.datasets = [{
                label: vm.$t('common.injuries'),
                data,
                details,
                ...vm.lineTemplate,
                backgroundColor: gradientFill,
                type: 'line',
                yAxisID: 'left-axis',
            }]
        },
    },
    watch: {
        groupDocs() {
            this.loadChart()
        },
        type() {
            this.loadChart()
        },
        docs() {
            this.loadChart()
        },
        '$vuetify.theme.dark'() {
            this.loadChart()
        }
    }
}
</script>

<style scoped>
.quantity-chart {
    display: flex;
    flex-direction: column;
    height: 100%;
    min-height: 0;
}

.qty-toolbar {
    display: flex;
    align-items: center;
    margin-bottom: 8px;
    flex-shrink: 0;
}

.qty-plot {
    flex: 1;
    min-height: 0;
}
</style>
